import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import { ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';

export interface RelatedCode {
  code: string;
  type: string; // CPT, HCPCS, DRG, REV, NDC
  description: string;
  setting?: string;
  hospitalCount?: number;
  price?: number;
  quantity?: number;
}

export interface RelatedScenario {
  id: string;
  name: string;
  slug: string;
  category?: string;
}

interface RelatedLinksProps {
  relatedCodes?: RelatedCode[];
  relatedScenarios?: RelatedScenario[];
}

const CODE_TYPE_ROUTES: Record<string, string> = {
  CPT: '/cpt',
  HCPCS: '/hcpcs',
  'MS-DRG': '/drg',
  DRG: '/drg',
  REV: '/revenue',
  NDC: '/drug',
};

const CODES_PER_PAGE = 3;
const SCENARIOS_PER_PAGE = 4;

export function RelatedLinks({ relatedCodes, relatedScenarios }: RelatedLinksProps) {
  const brand = useBrand();
  const isAhp = brand.key === 'ahp';
  const { isDark } = useTheme();
  const ahpDark = isAhp && isDark;
  const { t } = useTranslation();

  const [page, setPage] = useState(0);
  const [scenarioPage, setScenarioPage] = useState(0);

  const hasCodes = relatedCodes && relatedCodes.length > 0;
  const hasScenarios = relatedScenarios && relatedScenarios.length > 0;

  if (!hasCodes && !hasScenarios) return null;

  const totalPages = hasCodes ? Math.ceil(relatedCodes!.length / CODES_PER_PAGE) : 0;
  const pageCodes = hasCodes ? relatedCodes!.slice(page * CODES_PER_PAGE, (page + 1) * CODES_PER_PAGE) : [];

  const totalScenarioPages = hasScenarios ? Math.ceil(relatedScenarios!.length / SCENARIOS_PER_PAGE) : 0;
  const pageScenarios = hasScenarios ? relatedScenarios!.slice(scenarioPage * SCENARIOS_PER_PAGE, (scenarioPage + 1) * SCENARIOS_PER_PAGE) : [];

  return (
    <div className="space-y-4">
      {hasCodes && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4
              className="text-sm font-semibold uppercase tracking-wide"
              style={isAhp ? { color: ahpDark ? '#C5A55A' : '#002868', fontFamily: "'Instrument Sans', sans-serif" } : undefined}
            >
              {t('scenarios:related.proceduresInScenario')}
            </h4>
            <span
              className="text-[10px] font-medium"
              style={{ color: ahpDark ? '#667788' : '#999' }}
            >
              {t('scenarios:related.codesCount', { count: relatedCodes!.length })}
            </span>
          </div>

          <div className="space-y-2">
            {pageCodes.map((c, i) => {
              const route = CODE_TYPE_ROUTES[c.type] || '/cpt';
              const settingLabel = c.setting || t('scenarios:related.unspecified');
              const isInpatient = settingLabel.toLowerCase().includes('inpatient');

              return (
                <Link
                  key={`${c.type}-${c.code}-${page}-${i}`}
                  to={`${route}/${c.code}`}
                  className="group block rounded-lg border transition-colors"
                  style={{
                    backgroundColor: ahpDark ? 'rgba(255,255,255,0.03)' : isAhp ? 'rgba(0,40,104,0.02)' : undefined,
                    borderColor: ahpDark ? 'rgba(197,165,90,0.15)' : isAhp ? 'rgba(0,40,104,0.1)' : undefined,
                  }}
                >
                  {/* Row 1: Code badge + code number + price */}
                  <div className="flex items-center gap-2 px-3 pt-2.5 pb-1">
                    <Badge
                      variant="outline"
                      className="text-[10px] font-mono shrink-0 px-1.5 py-0"
                      style={isAhp ? { borderColor: ahpDark ? 'rgba(136,153,170,0.3)' : '#002868', color: ahpDark ? '#8899aa' : '#002868', backgroundColor: ahpDark ? 'rgba(136,153,170,0.08)' : undefined } : undefined}
                    >
                      {c.type}
                    </Badge>
                    <span className="text-sm font-mono font-bold" style={isAhp ? { color: ahpDark ? '#C5A55A' : '#002868' } : undefined}>
                      {c.code}
                    </span>
                    {c.quantity && c.quantity > 1 && (
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0" style={ahpDark ? { backgroundColor: 'rgba(197,165,90,0.15)', color: '#C5A55A', border: 'none' } : undefined}>
                        ×{c.quantity}
                      </Badge>
                    )}
                    <span className="flex-1" />
                    {c.price != null && c.price > 0 && (
                      <span className="text-sm font-mono font-bold" style={isAhp ? { color: ahpDark ? '#C5A55A' : '#002868' } : undefined}>
                        ${Math.round(c.price).toLocaleString()}
                      </span>
                    )}
                  </div>

                  {/* Row 2: Description */}
                  <div className="px-3 pb-1">
                    <p className="text-xs text-muted-foreground truncate">{c.description}</p>
                  </div>

                  {/* Row 3: Setting + hospital count + view link */}
                  <div className="flex items-center gap-2 px-3 pb-2.5">
                    {c.setting && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded" style={{
                        backgroundColor: isInpatient
                          ? (ahpDark ? 'rgba(232,93,108,0.12)' : 'rgba(178,34,52,0.08)')
                          : (ahpDark ? 'rgba(125,166,212,0.12)' : 'rgba(0,40,104,0.08)'),
                        color: isInpatient
                          ? (ahpDark ? '#e85d6c' : '#B22234')
                          : (ahpDark ? '#7da6d4' : '#002868'),
                      }}>
                        {settingLabel}
                      </span>
                    )}
                    {c.hospitalCount != null && c.hospitalCount > 0 && (
                      <span className="text-[10px]" style={{ color: ahpDark ? '#667788' : '#999' }}>
                        {t('scenarios:related.hospitalsCount', { count: c.hospitalCount.toLocaleString() })}
                      </span>
                    )}
                    <span className="flex-1" />
                    <span
                      className="text-[10px] font-semibold flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ color: ahpDark ? '#C5A55A' : '#002868' }}
                    >
                      {t('scenarios:related.viewCode', { type: c.type })} <ExternalLink className="w-3 h-3" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-3 pt-2" style={{ borderTop: `1px solid ${ahpDark ? 'rgba(197,165,90,0.1)' : 'rgba(0,0,0,0.06)'}` }}>
              <button
                onClick={(e) => { e.preventDefault(); setPage(p => Math.max(0, p - 1)); }}
                disabled={page === 0}
                className="flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded transition-colors disabled:opacity-30"
                style={{ color: ahpDark ? '#C5A55A' : '#002868' }}
              >
                <ChevronLeft className="w-3.5 h-3.5" /> {t('scenarios:related.prev')}
              </button>
              <span className="text-[10px]" style={{ color: ahpDark ? '#667788' : '#999' }}>
                {page + 1} / {totalPages}
              </span>
              <button
                onClick={(e) => { e.preventDefault(); setPage(p => Math.min(totalPages - 1, p + 1)); }}
                disabled={page >= totalPages - 1}
                className="flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded transition-colors disabled:opacity-30"
                style={{ color: ahpDark ? '#C5A55A' : '#002868' }}
              >
                {t('scenarios:related.next')} <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      )}

      {hasScenarios && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4
              className="text-sm font-semibold uppercase tracking-wide"
              style={isAhp ? { color: ahpDark ? '#C5A55A' : '#002868', fontFamily: "'Instrument Sans', sans-serif" } : undefined}
            >
              {t('scenarios:related.scenariosUsingCode')}
            </h4>
            <span
              className="text-[10px] font-medium"
              style={{ color: ahpDark ? '#667788' : '#999' }}
            >
              {t('scenarios:related.scenariosCount', { count: relatedScenarios!.length })}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {pageScenarios.map((s) => (
              <Link
                key={s.id}
                to={`/scenarios/${s.id}/${s.slug}`}
                className="group flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors hover:border-blue-300"
                style={{
                  backgroundColor: ahpDark ? 'rgba(255,255,255,0.03)' : isAhp ? 'rgba(0,40,104,0.02)' : undefined,
                  borderColor: ahpDark ? 'rgba(197,165,90,0.15)' : isAhp ? 'rgba(0,40,104,0.1)' : undefined,
                }}
              >
                <div className="min-w-0 flex-1">
                  <span className="text-sm font-medium" style={isAhp ? { color: ahpDark ? '#f0ebe3' : '#1B1B1B' } : undefined}>
                    {s.name}
                  </span>
                  {s.category && (
                    <p className="text-xs text-muted-foreground">{s.category}</p>
                  )}
                </div>
                <ExternalLink className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
              </Link>
            ))}
          </div>

          {/* Scenario Pagination */}
          {totalScenarioPages > 1 && (
            <div className="flex items-center justify-between mt-3 pt-2" style={{ borderTop: `1px solid ${ahpDark ? 'rgba(197,165,90,0.1)' : 'rgba(0,0,0,0.06)'}` }}>
              <button
                onClick={(e) => { e.preventDefault(); setScenarioPage(p => Math.max(0, p - 1)); }}
                disabled={scenarioPage === 0}
                className="flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded transition-colors disabled:opacity-30"
                style={{ color: ahpDark ? '#C5A55A' : '#002868' }}
              >
                <ChevronLeft className="w-3.5 h-3.5" /> {t('scenarios:related.prev')}
              </button>
              <span className="text-[10px]" style={{ color: ahpDark ? '#667788' : '#999' }}>
                {scenarioPage + 1} / {totalScenarioPages}
              </span>
              <button
                onClick={(e) => { e.preventDefault(); setScenarioPage(p => Math.min(totalScenarioPages - 1, p + 1)); }}
                disabled={scenarioPage >= totalScenarioPages - 1}
                className="flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded transition-colors disabled:opacity-30"
                style={{ color: ahpDark ? '#C5A55A' : '#002868' }}
              >
                {t('scenarios:related.next')} <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
